import { css, LitElement, html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import '@aileron/icon';
import { property } from 'lit/decorators.js';

const lightThemeStyles = css`
:root,
:host,
:host::after,
:host::before {
  --size-w1: 1rem;
  --size-w2: 2rem;
  --size-w4: 4rem;
  --size-w8: 8rem;
  --size-w12: 12rem;
  --size-w16: 16rem;
  --size-w24: 24rem;
  --size-w32: 32rem;
  --size-w40: 40rem;
  --size-w48: 48rem;
  --size-w64: 64rem;
  --size-w80: 80rem;
  --size-w96: 96rem;
  --size-w180: 160rem;
  --breakpoint-xs: 390;
  --breakpoint-sm: 800;
  --breakpoint-md: 1280;
  --breakpoint-lg: 1440;
  --spacing-2: 0.125rem;
  --spacing-4: 0.25rem;
  --spacing-8: 0.5rem;
  --spacing-12: 0.75rem;
  --spacing-16: 1rem;
  --spacing-24: 1.5rem;
  --spacing-32: 2rem;
  --spacing-36: 2.25rem;
  --spacing-40: 2.5rem;
  --spacing-48: 3rem;
  --spacing-64: 4rem;
  --spacing-80: 5rem;
  --spacing-96: 6rem;
  --spacing-180: 11.25rem;
  --radius-sm: 0.25rem;
  --radius-lg: 0.5rem;
  --color-light-mode-background-container-default: #ffffff;
  --color-light-mode-background-container-secondary: #f9fafb;
  --color-light-mode-background-container-tertiary: #f3f5f7;
  --color-light-mode-background-selected-container-default: #ffffff;
  --color-light-mode-background-selected-container-secondary: #f9fafb;
  --color-light-mode-background-selected-container-tertiary: #f3f5f7;
  --color-light-mode-background-container-interactive: #0860c4;
  --color-light-mode-background-container-status-information: #0860c4;
  --color-light-mode-background-container-status-success: #316833;
  --color-light-mode-background-container-status-warning: #ae3f13;
  --color-light-mode-background-container-status-error: #cd1d32;
  --color-light-mode-background-container-status-information-on-container-default: #f5faff;
  --color-light-mode-background-container-status-information-on-container-secondary: #ecf4fe;
  --color-light-mode-background-container-status-information-on-container-tertiary: #e2effe;
  --color-light-mode-background-container-status-success-on-container-default: #f1f8f1;
  --color-light-mode-background-container-status-success-on-container-secondary: #eaf5eb;
  --color-light-mode-background-container-status-success-on-container-tertiary: #e3f2e4;
  --color-light-mode-background-container-status-warning-on-container-default: #fef8f6;
  --color-light-mode-background-container-status-warning-on-container-secondary: #fdf1ed;
  --color-light-mode-background-container-status-warning-on-container-tertiary: #fceae3;
  --color-light-mode-background-container-status-error-on-container-default: #fef6f7;
  --color-light-mode-background-container-status-error-on-container-secondaryv: #fcedef;
  --color-light-mode-background-container-status-error-on-container-tertiary: #fbe4e7;
  --color-light-mode-text-text-default: #101419;
  --color-light-mode-text-text-secondary: #50657c;
  --color-light-mode-text-text-disabled: #b4c1cf;
  --color-light-mode-text-text-interactive: #0860c4;
  --color-light-mode-text-text-on-container-interactive: #ffffff;
  --color-light-mode-text-text-status-information: #0860c4;
  --color-light-mode-text-text-status-success: #316833;
  --color-light-mode-text-text-status-warning: #ae3f13;
  --color-light-mode-text-text-status-error: #cd1d32;
  --color-light-mode-icon-icon-default: #101419;
  --color-light-mode-icon-icon-secondary: #50657c;
  --color-light-mode-icon-icon-disabled: #b4c1cf;
  --color-light-mode-icon-icon-interactive: #0860c4;
  --color-light-mode-icon-icon-on-container-interactive: #ffffff;
  --color-light-mode-icon-icon-status-information: #0a78f5;
  --color-light-mode-icon-icon-status-success: #418b44;
  --color-light-mode-icon-icon-status-warning: #dc5018;
  --color-light-mode-icon-icon-status-error: #e33b4e;
  --color-light-mode-border-border-default: #637e9c;
  --color-light-mode-border-border-secondary: #b4c1cf;
  --color-light-mode-border-border-tertiary: #e6eaef;
  --color-light-mode-border-border-interactive: #0860c4;
  --color-light-mode-border-border-selected: #0a78f5;
  --color-light-mode-border-border-status-information: #0a78f5;
  --color-light-mode-border-border-status-success: #418b44;
  --color-light-mode-border-border-status-warning: #dc5018;
  --color-light-mode-border-border-status-error: #e33b4e;
  --color-light-mode-interactive-hover-hover-on-container-default: #f9fafb;
  --color-light-mode-interactive-hover-hover-on-container-secondary: #f3f5f7;
  --color-light-mode-interactive-hover-hover-on-container-tertiary: #eceff3;
  --color-light-mode-interactive-hover-hover-on-interactive: #0a78f5;
  --color-light-mode-interactive-hover-hover-on-selected-container-default: #f5faff;
  --color-light-mode-interactive-hover-hover-on-selected-container-secondary: #ecf4fe;
  --color-light-mode-interactive-hover-hover-on-selected-container-tertiary: #e2effe;
  --color-light-mode-interactive-pressed-pressed-on-container-default: #f3f5f7;
  --color-light-mode-interactive-pressed-pressed-on-container-secondary: #eceff3;
  --color-light-mode-interactive-pressed-pressed-on-container-tertiary: #e6eaef;
  --color-light-mode-interactive-pressed-pressed-on-interactive: #3b93f7;
  --color-light-mode-interactive-pressed-pressed-on-selected-container-default: #ecf4fe;
  --color-light-mode-interactive-pressed-pressed-on-selected-container-secondary: #e2effe;
  --color-light-mode-interactive-pressed-pressed-on-selected-container-tertiary: #d8e9fd;
  --color-light-mode-interactive-focus-outline-focus: #0a78f5;
  --color-light-mode-interactive-disabled-disabled-on-container-interactive: #b4c1cf;
  --color-light-mode-interactive-disabled-disabled-on-border-interactive: #b4c1cf;
  --neutral-140: #ffffff;
  --neutral-130: #f9fafb;
  --neutral-120: #f3f5f7;
  --neutral-110: #eceff3;
  --neutral-100: #e6eaef;
  --neutral-090: #b4c1cf;
  --neutral-080: #8398af;
  --neutral-070: #637e9c;
  --neutral-060: #50657c;
  --neutral-050: #405164;
  --neutral-040: #303d4b;
  --neutral-030: #28323e;
  --neutral-020: #202832;
  --neutral-010: #181e25;
  --neutral-000: #101419;
  --blue-140: #ffffff;
  --blue-130: #f5faff;
  --blue-120: #ecf4fe;
  --blue-110: #e2effe;
  --blue-100: #d8e9fd;
  --blue-090: #89befa;
  --blue-080: #3b93f7;
  --blue-070: #0a78f5;
  --blue-060: #0860c4;
  --blue-050: #074d9d;
  --blue-040: #053976;
  --blue-030: #043062;
  --blue-020: #03264e;
  --blue-010: #021d3c;
  --blue-000: #021327;
  --green-140: #ffffff;
  --green-130: #f1f8f1;
  --green-120: #eaf5eb;
  --green-110: #e3f2e4;
  --green-100: #dcefdd;
  --green-080: #52ad55;
  --green-070: #418b44;
  --green-060: #316833;
  --green-040: #244c25;
  --green-030: #1d3e1e;
  --green-020: #173118;
  --green-010: #102311;
  --green-000: #0a150a;
  --orange-140: #ffffff;
  --orange-130: #fef8f6;
  --orange-120: #fdf1ed;
  --orange-110: #fceae3;
  --orange-100: #fbe4da;
  --orange-080: #ea6f3e;
  --orange-070: #dc5018;
  --orange-060: #ae3f13;
  --orange-040: #6e280c;
  --orange-030: #5c210a;
  --orange-020: #491b08;
  --orange-010: #371406;
  --orange-000: #250d04;
  --red-140: #ffffff;
  --red-130: #fef6f7;
  --red-120: #fcedef;
  --red-110: #fbe4e7;
  --red-100: #fbe0e3;
  --red-080: #e96776;
  --red-070: #e33b4e;
  --red-060: #cd1d32;
  --red-040: #74111c;
  --red-030: #620e18;
  --red-020: #500b14;
  --red-010: #3e090f;
  --red-000: #2d060b;
  --grid-390-column-gutter-size: 1rem;
  --grid-390-column-count: 4;
  --grid-390-column-margin: 1rem;
  --grid-800-column-gutter-size: 1.5rem;
  --grid-800-column-count: 8;
  --grid-800-column-margin: 2rem;
  --grid-1280-column-gutter-size: 1.5rem;
  --grid-1280-column-count: 12;
  --grid-1280-column-margin: 2rem;
  --grid-1440-column-gutter-size: 1.5rem;
  --grid-1440-column-count: 12;
  --grid-1440-column-margin: auto;
  --elevation-01: 0px 3px 1px 0px rgba(0 0 0 / 0.12), 0px 2px 2px 0px rgba(0 0 0 / 0.14), 0px 1px 5px 0px rgba(0 0 0 / 0.2);
  --elevation-02: 0px 1px 18px 0px rgba(0 0 0 / 0.12), 0px 6px 10px 0px rgba(0 0 0 / 0.14), 0px 3px 5px -1px rgba(0 0 0 / 0.2);
  --elevation-03: 0px 3px 14px 2px rgba(0 0 0 / 0.12), 0px 8px 10px 1px rgba(0 0 0 / 0.14), 0px 5px 5px -3px rgba(0 0 0 / 0.2);
  --elevation-04: 0px 5px 22px 4px rgba(0 0 0 / 0.12), 0px 12px 17px 2px rgba(0 0 0 / 0.14), 0px 7px 8px -4px rgba(0 0 0 / 0.2);
  --elevation-05: 0px 9px 46px 8px rgba(0 0 0 / 0.12), 0px 24px 38px 3px rgba(0 0 0 / 0.14), 0px 11px 15px -7px rgba(0 0 0 / 0.2);
  --typography-390-heading-title-font-size: 36;
  --typography-390-heading-title-text-decoration: none;
  --typography-390-heading-title-font-family: AmericanSans;
  --typography-390-heading-title-font-weight: 400;
  --typography-390-heading-title-font-style: normal;
  --typography-390-heading-title-font-stretch: normal;
  --typography-390-heading-title-letter-spacing: 0;
  --typography-390-heading-title-line-height: 48;
  --typography-390-heading-title-paragraph-indent: 0;
  --typography-390-heading-title-paragraph-spacing: 0;
  --typography-390-heading-title-text-case: none;
  --typography-390-heading-h1-font-size: 32;
  --typography-390-heading-h1-text-decoration: none;
  --typography-390-heading-h1-font-family: AmericanSans;
  --typography-390-heading-h1-font-weight: 400;
  --typography-390-heading-h1-font-style: normal;
  --typography-390-heading-h1-font-stretch: normal;
  --typography-390-heading-h1-letter-spacing: 0;
  --typography-390-heading-h1-line-height: 40;
  --typography-390-heading-h1-paragraph-indent: 0;
  --typography-390-heading-h1-paragraph-spacing: 0;
  --typography-390-heading-h1-text-case: none;
  --typography-390-heading-h2-font-size: 28;
  --typography-390-heading-h2-text-decoration: none;
  --typography-390-heading-h2-font-family: AmericanSans;
  --typography-390-heading-h2-font-weight: 400;
  --typography-390-heading-h2-font-style: normal;
  --typography-390-heading-h2-font-stretch: normal;
  --typography-390-heading-h2-letter-spacing: 0;
  --typography-390-heading-h2-line-height: 36;
  --typography-390-heading-h2-paragraph-indent: 0;
  --typography-390-heading-h2-paragraph-spacing: 0;
  --typography-390-heading-h2-text-case: none;
  --typography-390-heading-h3-font-size: 24;
  --typography-390-heading-h3-text-decoration: none;
  --typography-390-heading-h3-font-family: AmericanSans;
  --typography-390-heading-h3-font-weight: 400;
  --typography-390-heading-h3-font-style: normal;
  --typography-390-heading-h3-font-stretch: normal;
  --typography-390-heading-h3-letter-spacing: 0;
  --typography-390-heading-h3-line-height: 32;
  --typography-390-heading-h3-paragraph-indent: 0;
  --typography-390-heading-h3-paragraph-spacing: 0;
  --typography-390-heading-h3-text-case: none;
  --typography-390-heading-h4-font-size: 20;
  --typography-390-heading-h4-text-decoration: none;
  --typography-390-heading-h4-font-family: AmericanSans;
  --typography-390-heading-h4-font-weight: 400;
  --typography-390-heading-h4-font-style: normal;
  --typography-390-heading-h4-font-stretch: normal;
  --typography-390-heading-h4-letter-spacing: 0;
  --typography-390-heading-h4-line-height: 24;
  --typography-390-heading-h4-paragraph-indent: 0;
  --typography-390-heading-h4-paragraph-spacing: 0;
  --typography-390-heading-h4-text-case: none;
  --typography-390-heading-h5-font-size: 18;
  --typography-390-heading-h5-text-decoration: none;
  --typography-390-heading-h5-font-family: AmericanSans;
  --typography-390-heading-h5-font-weight: 400;
  --typography-390-heading-h5-font-style: normal;
  --typography-390-heading-h5-font-stretch: normal;
  --typography-390-heading-h5-letter-spacing: 0;
  --typography-390-heading-h5-line-height: 24;
  --typography-390-heading-h5-paragraph-indent: 0;
  --typography-390-heading-h5-paragraph-spacing: 0;
  --typography-390-heading-h5-text-case: none;
  --typography-390-body-paragraph-font-size: 16;
  --typography-390-body-paragraph-text-decoration: none;
  --typography-390-body-paragraph-font-family: AmericanSans;
  --typography-390-body-paragraph-font-weight: 400;
  --typography-390-body-paragraph-font-style: normal;
  --typography-390-body-paragraph-font-stretch: normal;
  --typography-390-body-paragraph-letter-spacing: 0.16;
  --typography-390-body-paragraph-line-height: 22;
  --typography-390-body-paragraph-paragraph-indent: 0;
  --typography-390-body-paragraph-paragraph-spacing: 0;
  --typography-390-body-paragraph-text-case: none;
  --typography-390-body-paragraph-bold-font-size: 16;
  --typography-390-body-paragraph-bold-text-decoration: none;
  --typography-390-body-paragraph-bold-font-family: AmericanSans;
  --typography-390-body-paragraph-bold-font-weight: 700;
  --typography-390-body-paragraph-bold-font-style: normal;
  --typography-390-body-paragraph-bold-font-stretch: normal;
  --typography-390-body-paragraph-bold-letter-spacing: 0.16;
  --typography-390-body-paragraph-bold-line-height: 22;
  --typography-390-body-paragraph-bold-paragraph-indent: 0;
  --typography-390-body-paragraph-bold-paragraph-spacing: 0;
  --typography-390-body-paragraph-bold-text-case: none;
  --typography-390-body-paragraph-underline-font-size: 16;
  --typography-390-body-paragraph-underline-text-decoration: underline;
  --typography-390-body-paragraph-underline-font-family: AmericanSans;
  --typography-390-body-paragraph-underline-font-weight: 400;
  --typography-390-body-paragraph-underline-font-style: normal;
  --typography-390-body-paragraph-underline-font-stretch: normal;
  --typography-390-body-paragraph-underline-letter-spacing: 0.16;
  --typography-390-body-paragraph-underline-line-height: 22;
  --typography-390-body-paragraph-underline-paragraph-indent: 0;
  --typography-390-body-paragraph-underline-paragraph-spacing: 0;
  --typography-390-body-paragraph-underline-text-case: none;
  --typography-390-body-lead-font-size: 18;
  --typography-390-body-lead-text-decoration: none;
  --typography-390-body-lead-font-family: AmericanSans;
  --typography-390-body-lead-font-weight: 400;
  --typography-390-body-lead-font-style: normal;
  --typography-390-body-lead-font-stretch: normal;
  --typography-390-body-lead-letter-spacing: 0.16;
  --typography-390-body-lead-line-height: 24;
  --typography-390-body-lead-paragraph-indent: 0;
  --typography-390-body-lead-paragraph-spacing: 0;
  --typography-390-body-lead-text-case: none;
  --typography-390-body-lead-bold-font-size: 18;
  --typography-390-body-lead-bold-text-decoration: none;
  --typography-390-body-lead-bold-font-family: AmericanSans;
  --typography-390-body-lead-bold-font-weight: 700;
  --typography-390-body-lead-bold-font-style: normal;
  --typography-390-body-lead-bold-font-stretch: normal;
  --typography-390-body-lead-bold-letter-spacing: 0.16;
  --typography-390-body-lead-bold-line-height: 24;
  --typography-390-body-lead-bold-paragraph-indent: 0;
  --typography-390-body-lead-bold-paragraph-spacing: 0;
  --typography-390-body-lead-bold-text-case: none;
  --typography-390-body-lead-underline-font-size: 18;
  --typography-390-body-lead-underline-text-decoration: underline;
  --typography-390-body-lead-underline-font-family: AmericanSans;
  --typography-390-body-lead-underline-font-weight: 400;
  --typography-390-body-lead-underline-font-style: normal;
  --typography-390-body-lead-underline-font-stretch: normal;
  --typography-390-body-lead-underline-letter-spacing: 0.16;
  --typography-390-body-lead-underline-line-height: 24;
  --typography-390-body-lead-underline-paragraph-indent: 0;
  --typography-390-body-lead-underline-paragraph-spacing: 0;
  --typography-390-body-lead-underline-text-case: none;
  --typography-390-label-label-font-size: 14;
  --typography-390-label-label-text-decoration: none;
  --typography-390-label-label-font-family: AmericanSans;
  --typography-390-label-label-font-weight: 400;
  --typography-390-label-label-font-style: normal;
  --typography-390-label-label-font-stretch: normal;
  --typography-390-label-label-letter-spacing: 0.16;
  --typography-390-label-label-line-height: 16;
  --typography-390-label-label-paragraph-indent: 0;
  --typography-390-label-label-paragraph-spacing: 0;
  --typography-390-label-label-text-case: none;
  --typography-390-label-label-caps-font-size: 14;
  --typography-390-label-label-caps-text-decoration: none;
  --typography-390-label-label-caps-font-family: AmericanSans;
  --typography-390-label-label-caps-font-weight: 400;
  --typography-390-label-label-caps-font-style: normal;
  --typography-390-label-label-caps-font-stretch: normal;
  --typography-390-label-label-caps-letter-spacing: 0.16;
  --typography-390-label-label-caps-line-height: 16;
  --typography-390-label-label-caps-paragraph-indent: 0;
  --typography-390-label-label-caps-paragraph-spacing: 0;
  --typography-390-label-label-caps-text-case: uppercase;
  --typography-390-label-label-medium-font-size: 14;
  --typography-390-label-label-medium-text-decoration: none;
  --typography-390-label-label-medium-font-family: AmericanSans Medium;
  --typography-390-label-label-medium-font-weight: 400;
  --typography-390-label-label-medium-font-style: normal;
  --typography-390-label-label-medium-font-stretch: normal;
  --typography-390-label-label-medium-letter-spacing: 0.16;
  --typography-390-label-label-medium-line-height: 16;
  --typography-390-label-label-medium-paragraph-indent: 0;
  --typography-390-label-label-medium-paragraph-spacing: 0;
  --typography-390-label-label-medium-text-case: none;
  --typography-390-label-label-medium-caps-font-size: 14;
  --typography-390-label-label-medium-caps-text-decoration: none;
  --typography-390-label-label-medium-caps-font-family: AmericanSans Medium;
  --typography-390-label-label-medium-caps-font-weight: 400;
  --typography-390-label-label-medium-caps-font-style: normal;
  --typography-390-label-label-medium-caps-font-stretch: normal;
  --typography-390-label-label-medium-caps-letter-spacing: 0.16;
  --typography-390-label-label-medium-caps-line-height: 16;
  --typography-390-label-label-medium-caps-paragraph-indent: 0;
  --typography-390-label-label-medium-caps-paragraph-spacing: 0;
  --typography-390-label-label-medium-caps-text-case: uppercase;
  --typography-390-helper-caption-font-size: 12;
  --typography-390-helper-caption-text-decoration: none;
  --typography-390-helper-caption-font-family: AmericanSans;
  --typography-390-helper-caption-font-weight: 400;
  --typography-390-helper-caption-font-style: normal;
  --typography-390-helper-caption-font-stretch: normal;
  --typography-390-helper-caption-letter-spacing: 0;
  --typography-390-helper-caption-line-height: 16;
  --typography-390-helper-caption-paragraph-indent: 0;
  --typography-390-helper-caption-paragraph-spacing: 0;
  --typography-390-helper-caption-text-case: none;
  --typography-390-helper-caption-medium-font-size: 12;
  --typography-390-helper-caption-medium-text-decoration: none;
  --typography-390-helper-caption-medium-font-family: AmericanSans Medium;
  --typography-390-helper-caption-medium-font-weight: 400;
  --typography-390-helper-caption-medium-font-style: normal;
  --typography-390-helper-caption-medium-font-stretch: normal;
  --typography-390-helper-caption-medium-letter-spacing: 0;
  --typography-390-helper-caption-medium-line-height: 16;
  --typography-390-helper-caption-medium-paragraph-indent: 0;
  --typography-390-helper-caption-medium-paragraph-spacing: 0;
  --typography-390-helper-caption-medium-text-case: none;
  --typography-1440-heading-title-font-size: 54;
  --typography-1440-heading-title-text-decoration: none;
  --typography-1440-heading-title-font-family: AmericanSans;
  --typography-1440-heading-title-font-weight: 400;
  --typography-1440-heading-title-font-style: normal;
  --typography-1440-heading-title-font-stretch: normal;
  --typography-1440-heading-title-letter-spacing: 0;
  --typography-1440-heading-title-line-height: 64;
  --typography-1440-heading-title-paragraph-indent: 0;
  --typography-1440-heading-title-paragraph-spacing: 0;
  --typography-1440-heading-title-text-case: none;
  --typography-1440-heading-h1-font-size: 42;
  --typography-1440-heading-h1-text-decoration: none;
  --typography-1440-heading-h1-font-family: AmericanSans;
  --typography-1440-heading-h1-font-weight: 400;
  --typography-1440-heading-h1-font-style: normal;
  --typography-1440-heading-h1-font-stretch: normal;
  --typography-1440-heading-h1-letter-spacing: 0;
  --typography-1440-heading-h1-line-height: 50;
  --typography-1440-heading-h1-paragraph-indent: 0;
  --typography-1440-heading-h1-paragraph-spacing: 0;
  --typography-1440-heading-h1-text-case: none;
  --typography-1440-heading-h2-font-size: 32;
  --typography-1440-heading-h2-text-decoration: none;
  --typography-1440-heading-h2-font-family: AmericanSans;
  --typography-1440-heading-h2-font-weight: 400;
  --typography-1440-heading-h2-font-style: normal;
  --typography-1440-heading-h2-font-stretch: normal;
  --typography-1440-heading-h2-letter-spacing: 0;
  --typography-1440-heading-h2-line-height: 40;
  --typography-1440-heading-h2-paragraph-indent: 0;
  --typography-1440-heading-h2-paragraph-spacing: 0;
  --typography-1440-heading-h2-text-case: none;
  --typography-1440-heading-h3-font-size: 28;
  --typography-1440-heading-h3-text-decoration: none;
  --typography-1440-heading-h3-font-family: AmericanSans;
  --typography-1440-heading-h3-font-weight: 400;
  --typography-1440-heading-h3-font-style: normal;
  --typography-1440-heading-h3-font-stretch: normal;
  --typography-1440-heading-h3-letter-spacing: 0;
  --typography-1440-heading-h3-line-height: 36;
  --typography-1440-heading-h3-paragraph-indent: 0;
  --typography-1440-heading-h3-paragraph-spacing: 0;
  --typography-1440-heading-h3-text-case: none;
  --typography-1440-heading-h4-font-size: 24;
  --typography-1440-heading-h4-text-decoration: none;
  --typography-1440-heading-h4-font-family: AmericanSans;
  --typography-1440-heading-h4-font-weight: 400;
  --typography-1440-heading-h4-font-style: normal;
  --typography-1440-heading-h4-font-stretch: normal;
  --typography-1440-heading-h4-letter-spacing: 0;
  --typography-1440-heading-h4-line-height: 32;
  --typography-1440-heading-h4-paragraph-indent: 0;
  --typography-1440-heading-h4-paragraph-spacing: 0;
  --typography-1440-heading-h4-text-case: none;
  --typography-1440-heading-h5-font-size: 20;
  --typography-1440-heading-h5-text-decoration: none;
  --typography-1440-heading-h5-font-family: AmericanSans;
  --typography-1440-heading-h5-font-weight: 400;
  --typography-1440-heading-h5-font-style: normal;
  --typography-1440-heading-h5-font-stretch: normal;
  --typography-1440-heading-h5-letter-spacing: 0;
  --typography-1440-heading-h5-line-height: 24;
  --typography-1440-heading-h5-paragraph-indent: 0;
  --typography-1440-heading-h5-paragraph-spacing: 0;
  --typography-1440-heading-h5-text-case: none;
  --typography-1440-body-paragraph-font-size: 16;
  --typography-1440-body-paragraph-text-decoration: none;
  --typography-1440-body-paragraph-font-family: AmericanSans;
  --typography-1440-body-paragraph-font-weight: 400;
  --typography-1440-body-paragraph-font-style: normal;
  --typography-1440-body-paragraph-font-stretch: normal;
  --typography-1440-body-paragraph-letter-spacing: 0.16;
  --typography-1440-body-paragraph-line-height: 24;
  --typography-1440-body-paragraph-paragraph-indent: 0;
  --typography-1440-body-paragraph-paragraph-spacing: 0;
  --typography-1440-body-paragraph-text-case: none;
  --typography-1440-body-paragraph-bold-font-size: 16;
  --typography-1440-body-paragraph-bold-text-decoration: none;
  --typography-1440-body-paragraph-bold-font-family: AmericanSans;
  --typography-1440-body-paragraph-bold-font-weight: 700;
  --typography-1440-body-paragraph-bold-font-style: normal;
  --typography-1440-body-paragraph-bold-font-stretch: normal;
  --typography-1440-body-paragraph-bold-letter-spacing: 0.16;
  --typography-1440-body-paragraph-bold-line-height: 24;
  --typography-1440-body-paragraph-bold-paragraph-indent: 0;
  --typography-1440-body-paragraph-bold-paragraph-spacing: 0;
  --typography-1440-body-paragraph-bold-text-case: none;
  --typography-1440-body-paragraph-underline-font-size: 16;
  --typography-1440-body-paragraph-underline-text-decoration: underline;
  --typography-1440-body-paragraph-underline-font-family: AmericanSans;
  --typography-1440-body-paragraph-underline-font-weight: 400;
  --typography-1440-body-paragraph-underline-font-style: normal;
  --typography-1440-body-paragraph-underline-font-stretch: normal;
  --typography-1440-body-paragraph-underline-letter-spacing: 0.16;
  --typography-1440-body-paragraph-underline-line-height: 24;
  --typography-1440-body-paragraph-underline-paragraph-indent: 0;
  --typography-1440-body-paragraph-underline-paragraph-spacing: 0;
  --typography-1440-body-paragraph-underline-text-case: none;
  --typography-1440-body-lead-font-size: 18;
  --typography-1440-body-lead-text-decoration: none;
  --typography-1440-body-lead-font-family: AmericanSans;
  --typography-1440-body-lead-font-weight: 400;
  --typography-1440-body-lead-font-style: normal;
  --typography-1440-body-lead-font-stretch: normal;
  --typography-1440-body-lead-letter-spacing: 0.16;
  --typography-1440-body-lead-line-height: 26;
  --typography-1440-body-lead-paragraph-indent: 0;
  --typography-1440-body-lead-paragraph-spacing: 0;
  --typography-1440-body-lead-text-case: none;
  --typography-1440-body-lead-bold-font-size: 18;
  --typography-1440-body-lead-bold-text-decoration: none;
  --typography-1440-body-lead-bold-font-family: AmericanSans;
  --typography-1440-body-lead-bold-font-weight: 700;
  --typography-1440-body-lead-bold-font-style: normal;
  --typography-1440-body-lead-bold-font-stretch: normal;
  --typography-1440-body-lead-bold-letter-spacing: 0.16;
  --typography-1440-body-lead-bold-line-height: 26;
  --typography-1440-body-lead-bold-paragraph-indent: 0;
  --typography-1440-body-lead-bold-paragraph-spacing: 0;
  --typography-1440-body-lead-bold-text-case: none;
  --typography-1440-body-lead-underline-font-size: 18;
  --typography-1440-body-lead-underline-text-decoration: underline;
  --typography-1440-body-lead-underline-font-family: AmericanSans;
  --typography-1440-body-lead-underline-font-weight: 400;
  --typography-1440-body-lead-underline-font-style: normal;
  --typography-1440-body-lead-underline-font-stretch: normal;
  --typography-1440-body-lead-underline-letter-spacing: 0.16;
  --typography-1440-body-lead-underline-line-height: 26;
  --typography-1440-body-lead-underline-paragraph-indent: 0;
  --typography-1440-body-lead-underline-paragraph-spacing: 0;
  --typography-1440-body-lead-underline-text-case: none;
  --typography-1440-label-label-font-size: 14;
  --typography-1440-label-label-text-decoration: none;
  --typography-1440-label-label-font-family: AmericanSans;
  --typography-1440-label-label-font-weight: 400;
  --typography-1440-label-label-font-style: normal;
  --typography-1440-label-label-font-stretch: normal;
  --typography-1440-label-label-letter-spacing: 0.16;
  --typography-1440-label-label-line-height: 16;
  --typography-1440-label-label-paragraph-indent: 0;
  --typography-1440-label-label-paragraph-spacing: 0;
  --typography-1440-label-label-text-case: none;
  --typography-1440-label-label-caps-font-size: 14;
  --typography-1440-label-label-caps-text-decoration: none;
  --typography-1440-label-label-caps-font-family: AmericanSans;
  --typography-1440-label-label-caps-font-weight: 400;
  --typography-1440-label-label-caps-font-style: normal;
  --typography-1440-label-label-caps-font-stretch: normal;
  --typography-1440-label-label-caps-letter-spacing: 0.16;
  --typography-1440-label-label-caps-line-height: 16;
  --typography-1440-label-label-caps-paragraph-indent: 0;
  --typography-1440-label-label-caps-paragraph-spacing: 0;
  --typography-1440-label-label-caps-text-case: uppercase;
  --typography-1440-label-label-medium-font-size: 14;
  --typography-1440-label-label-medium-text-decoration: none;
  --typography-1440-label-label-medium-font-family: AmericanSans Medium;
  --typography-1440-label-label-medium-font-weight: 400;
  --typography-1440-label-label-medium-font-style: normal;
  --typography-1440-label-label-medium-font-stretch: normal;
  --typography-1440-label-label-medium-letter-spacing: 0.16;
  --typography-1440-label-label-medium-line-height: 16;
  --typography-1440-label-label-medium-paragraph-indent: 0;
  --typography-1440-label-label-medium-paragraph-spacing: 0;
  --typography-1440-label-label-medium-text-case: none;
  --typography-1440-label-label-medium-caps-font-size: 14;
  --typography-1440-label-label-medium-caps-text-decoration: none;
  --typography-1440-label-label-medium-caps-font-family: AmericanSans Medium;
  --typography-1440-label-label-medium-caps-font-weight: 400;
  --typography-1440-label-label-medium-caps-font-style: normal;
  --typography-1440-label-label-medium-caps-font-stretch: normal;
  --typography-1440-label-label-medium-caps-letter-spacing: 0.16;
  --typography-1440-label-label-medium-caps-line-height: 16;
  --typography-1440-label-label-medium-caps-paragraph-indent: 0;
  --typography-1440-label-label-medium-caps-paragraph-spacing: 0;
  --typography-1440-label-label-medium-caps-text-case: uppercase;
  --typography-1440-helper-caption-font-size: 12;
  --typography-1440-helper-caption-text-decoration: none;
  --typography-1440-helper-caption-font-family: AmericanSans;
  --typography-1440-helper-caption-font-weight: 400;
  --typography-1440-helper-caption-font-style: normal;
  --typography-1440-helper-caption-font-stretch: normal;
  --typography-1440-helper-caption-letter-spacing: 0;
  --typography-1440-helper-caption-line-height: 16;
  --typography-1440-helper-caption-paragraph-indent: 0;
  --typography-1440-helper-caption-paragraph-spacing: 0;
  --typography-1440-helper-caption-text-case: none;
  --typography-1440-helper-caption-medium-font-size: 12;
  --typography-1440-helper-caption-medium-text-decoration: none;
  --typography-1440-helper-caption-medium-font-family: AmericanSans Medium;
  --typography-1440-helper-caption-medium-font-weight: 400;
  --typography-1440-helper-caption-medium-font-style: normal;
  --typography-1440-helper-caption-medium-font-stretch: normal;
  --typography-1440-helper-caption-medium-letter-spacing: 0;
  --typography-1440-helper-caption-medium-line-height: 16;
  --typography-1440-helper-caption-medium-paragraph-indent: 0;
  --typography-1440-helper-caption-medium-paragraph-spacing: 0;
  --typography-1440-helper-caption-medium-text-case: none;
}`;

const componentStyles = css`
  ${lightThemeStyles}

  :root,
  :host {
    --spacing-20: 1.25rem;
    --animation-speed: 50ms;
    --animation-easing: ease-in;
  }

  :root [disabled],
  :host [disabled] {
    --color-light-mode-interactive-disabled: #b4c1cf;
  }

  :host {
    box-sizing: border-box;
    -moz-osx-font-smoothing: grayscale;
    -webkit-font-smoothing: antialiased;
  }

  :host *,
  :host *::before,
  :host *::after {
    box-sizing: inherit;
  }

  :host *:focus-visible {
    outline: none;
  }

  [hidden] {
    display: none !important;
  }

  [disabled] {
    pointer-events: none;
  }

  :host .sr-only {
    clip: rect(1px, 1px, 1px, 1px);
    clip-path: inset(50%);
    height: 1px;
    width: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
  }
`;

const styles = css`*,::before,::after{box-sizing:border-box}::before,::after{--adc-content:''}html{line-height:1.5;-webkit-text-size-adjust:100%;-o-tab-size:4;tab-size:4;font-family:ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"}body{margin:0;line-height:inherit}hr{height:0;color:inherit;border-top-width:1px}abbr:where([title]){-webkit-text-decoration:underline dotted;text-decoration:underline dotted}h1,h2,h3,h4,h5,h6{font-size:inherit;font-weight:inherit}a{color:inherit;text-decoration:inherit}b,strong{font-weight:bolder}code,kbd,samp,pre{font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,"Liberation Mono","Courier New",monospace;font-size:1em}small{font-size:80%}sub,sup{font-size:75%;line-height:0;position:relative;vertical-align:baseline}sub{bottom:-0.25em}sup{top:-0.5em}table{text-indent:0;border-color:inherit;border-collapse:collapse}button,input,optgroup,select,textarea{font-family:inherit;font-size:100%;font-weight:inherit;line-height:inherit;color:inherit;margin:0;padding:0;border:0}button,select{text-transform:none}button,[type='button'],[type='reset'],[type='submit']{-webkit-appearance:button;background-color:transparent;background-image:none}:-moz-focusring{outline:auto}:-moz-ui-invalid{box-shadow:none}progress{vertical-align:baseline}::-webkit-inner-spin-button,::-webkit-outer-spin-button{height:auto}[type='search']{-webkit-appearance:textfield;outline-offset:-2px}::-webkit-search-decoration{-webkit-appearance:none}::-webkit-file-upload-button{-webkit-appearance:button;font:inherit}summary{display:list-item}blockquote,dl,dd,h1,h2,h3,h4,h5,h6,hr,figure,p,pre{margin:0}fieldset{margin:0;padding:0}legend{padding:0}ol,ul,menu{list-style:none;margin:0;padding:0}textarea{resize:vertical}input::-webkit-input-placeholder,textarea::-webkit-input-placeholder{opacity:1;color:#9ca3af}input:-ms-input-placeholder,textarea:-ms-input-placeholder{opacity:1;color:#9ca3af}input::placeholder,textarea::placeholder{opacity:1;color:#9ca3af}img,svg,video,canvas,audio,iframe,embed,object{display:block;vertical-align:middle}img,video{max-width:100%;height:auto}*,::before,::after{--adc-border-spacing-x:0;--adc-border-spacing-y:0;--adc-translate-x:0;--adc-translate-y:0;--adc-rotate:0;--adc-skew-x:0;--adc-skew-y:0;--adc-scale-x:1;--adc-scale-y:1;--adc-pan-x:;--adc-pan-y:;--adc-pinch-zoom:;--adc-scroll-snap-strictness:proximity;--adc-shadow:0 0 #0000;--adc-shadow-colored:0 0 #0000}::-webkit-backdrop{--adc-border-spacing-x:0;--adc-border-spacing-y:0;--adc-translate-x:0;--adc-translate-y:0;--adc-rotate:0;--adc-skew-x:0;--adc-skew-y:0;--adc-scale-x:1;--adc-scale-y:1;--adc-pan-x:;--adc-pan-y:;--adc-pinch-zoom:;--adc-scroll-snap-strictness:proximity;--adc-shadow:0 0 #0000;--adc-shadow-colored:0 0 #0000}::backdrop{--adc-border-spacing-x:0;--adc-border-spacing-y:0;--adc-translate-x:0;--adc-translate-y:0;--adc-rotate:0;--adc-skew-x:0;--adc-skew-y:0;--adc-scale-x:1;--adc-scale-y:1;--adc-pan-x:;--adc-pan-y:;--adc-pinch-zoom:;--adc-scroll-snap-strictness:proximity;--adc-shadow:0 0 #0000;--adc-shadow-colored:0 0 #0000}.static{position:static}.relative{position:relative}.-top-\\[3px\\]{top:-3px}.m-0{margin:0}.inline{display:inline}.list-item{display:list-item}.hidden{display:none}.list-decimal{list-style-type:decimal}.list-\\[lower-latin\\]{list-style-type:lower-latin}.list-disc{list-style-type:disc}.list-\\[circle\\]{list-style-type:circle}.text-neutral-000{color:#101419}.text-green-070{color:#418b44}.text-red-070{color:#e33b4e}:host(adc-list-item){font-family:var(--typography-1440-body-paragraph-font-family);font-size:calc(var(--typography-1440-body-paragraph-font-size) * 1px);font-weight:var(--typography-1440-body-paragraph-font-weight);letter-spacing:calc(var(--typography-1440-body-paragraph-letter-spacing) * 1px);line-height:var(--spacing-24)}:host(adc-list-item[kind="error"]) .adc-list-item::marker,:host(adc-list-item[kind="success"]) .adc-list-item::marker{content:""}ol,ul{-webkit-padding-start:var(--spacing-24);padding-inline-start:var(--spacing-24)}`;

class UnorderedList extends LitElement {
  connectedCallback() {
    if (this.closest(this.constructor.selectorListItem)) {
      this.setAttribute("slot", "nested");
    } else {
      this.removeAttribute("slot");
    }
    super.connectedCallback();
  }
  render() {
    return html`
      <ul
        class="list-disc ps-24 m-0 ${classMap({
      "list-[circle]": this.getAttribute("slot") === "nested"
    })}"
      >
        <slot></slot>
      </ul>
    `;
  }
  static get selectorListItem() {
    return "adc-list-item";
  }
}
UnorderedList.styles = [componentStyles, styles];
try {
  customElements.define("adc-unordered-list", UnorderedList);
} catch (e) {}

class OrderedList extends UnorderedList {
  render() {
    return html`
      <ol
        class="list-decimal ps-24 m-0 ${classMap({
      "list-[lower-latin]": this.getAttribute("slot") === "nested"
    })}"
      >
        <slot></slot>
      </ol>
    `;
  }
}
OrderedList.styles = [componentStyles, styles];
try {
  customElements.define("adc-ordered-list", OrderedList);
} catch (e) {}

var ICON_KIND = /* @__PURE__ */(ICON_KIND2 => {
  ICON_KIND2["SUCCESS"] = "success";
  ICON_KIND2["ERROR"] = "error";
  ICON_KIND2["DEFAULT"] = "default";
  return ICON_KIND2;
})(ICON_KIND || {});

var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--) if (decorator = decorators[i]) result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
class ListItem extends LitElement {
  constructor() {
    super(...arguments);
    this.kind = ICON_KIND.DEFAULT;
    this.nested = false;
    this._hasNestedChild = false;
  }
  _handleSlotChangeNested({
    target
  }) {
    this._hasNestedChild = target.assignedNodes().length > 0;
    this.requestUpdate();
  }
  connectedCallback() {
    this.toggleAttribute("nested", !!this.closest(this.constructor.selectorNestedList));
    if (!this.hasAttribute("role")) {
      this.setAttribute("role", "listitem");
    }
    super.connectedCallback();
  }
  render() {
    const {
      _hasNestedChild: hasNestedChild,
      _handleSlotChangeNested: handleSlotChangeNested
    } = this;
    return html`
      <div class="adc-list-item list-item text-neutral-000">
        <adc-icon
          icon="signal:checkmark"
          class="relative -top-[3px] text-green-070 ${classMap({
      inline: this.kind === "success",
      hidden: this.kind !== "success"
    })}"
        ></adc-icon>
        <adc-icon
          icon="action:close"
          class="relative -top-[3px] text-red-070 ${classMap({
      inline: this.kind === "error",
      hidden: this.kind !== "error"
    })}"
        ></adc-icon>
        <slot></slot>
        <div ?hidden="${!hasNestedChild}">
          <slot name="nested" @slotchange="${handleSlotChangeNested}"></slot>
        </div>
      </div>
    `;
  }
  static get selectorNestedList() {
    return 'adc-ordered-list[slot="nested"],adc-unordered-list[slot="nested"]';
  }
}
ListItem.styles = [componentStyles, styles];
__decorateClass([property({
  type: String,
  reflect: false
})], ListItem.prototype, "kind", 2);
__decorateClass([property({
  type: Boolean,
  reflect: true
})], ListItem.prototype, "nested", 2);
try {
  customElements.define("adc-list-item", ListItem);
} catch (e) {}
